import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { fetchInncodes, setInncodes } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import { clearUserDetail } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import { debounce } from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { ENTER_KEY_CODE, minSearchStrLength } from "utils/CommonConstants";
import Typography from "./Typography";

interface Props {
  onSelect: (...args: any) => void;
  size?: "small" | "medium";
  placeholder?: any;
  startIcon?: any;
  inline?: boolean;
  searchOnKeyPress?: boolean;
  isClearSearch?: boolean;
  innCodeList?: any;
}

const AutocompleteInncode = (props: Props) => {
  const { innCodeList } = props;
  const dispatch = useAppDispatch();
  const [inputValue, setInputValue] = useState("");
  const { isMobileScreen } = useContext(ThemeContext);
  const { innCodes } = useAppSelector((state) => state.app);
  const {
    onSelect,
    size = "medium",
    placeholder,
    startIcon = null,
    inline = false,
    searchOnKeyPress = false,
    isClearSearch = false
  } = props;

  const loadData = (input: any) => {
    if (input.length === 0) {
      dispatch(setInncodes([]));
      return;
    }

    if (input.length > minSearchStrLength) {
      dispatch(fetchInncodes(input));
    }
  };

  const debouncedLoadData = useRef(debounce(loadData, 500));

  const handleInputChange = (event: any, value: any, reason: string) => {
    dispatch(clearUserDetail());
    setInputValue(value);
    if (reason === "input") {
      if (searchOnKeyPress) {
        onSearch(event);
      } else {
        debouncedLoadData.current(value);
      }
    }
  };

  const handleChange = (e: any, v: any) => {
    if (v) {
      onSelect(v);
    }
    if (isClearSearch) setInputValue("");
  };

  useEffect(() => {
    if (!inputValue.trim()) {
      onSelect("");
    }
  }, [inputValue]);

  const onSearch = (e: any) => {
    if (e.charCode === ENTER_KEY_CODE || e.key === "Enter") {
      if (inputValue.trim() && inputValue.trim().length > minSearchStrLength) {
        dispatch(fetchInncodes(inputValue));
      }
    }
  };

  const handleCheckBoxChange = (e: any, value: any) => {
    const { name, checked } = e.target;

    if (checked) {
      onSelect({ checked, value });
    } else {
      onSelect({ checked, value: { innCode: name } });
    }
  };

  const innCodeOptions = () => {
    if (innCodeList) {
      return innCodes.filter((data: any) => !innCodeList.some((item: any) => item === data.innCode));
    }
    return innCodes;
  };

  const options = innCodeOptions();
  return inline ? (
    <>
      <TextField
        fullWidth
        onKeyUp={onSearch}
        value={inputValue}
        onBlur={() => dispatch(setInncodes([]))}
        onChange={(e: any) => setInputValue(e.target.value)}
        placeholder="Search by property or Inncode"
        sx={{ backgroundColor: tokens.Background.General.Primary }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: inputValue.length ? (
            <InputAdornment position="end">
              <CloseIcon sx={{ cursor: "pointer", fontSize: "20px" }} onClick={() => setInputValue("")} />
            </InputAdornment>
          ) : null
        }}
      />
      <Box component="ul" sx={{ maxHeight: isMobileScreen ? "100%" : "300px", overflowY: "auto", p: 0 }}>
        {innCodes.map((i: any) => (
          <Stack direction="row" alignItems="flex-start" component="li" key={i?.innCode} sx={{ py: "8px" }}>
            <Checkbox
              name={i?.innCode}
              size="small"
              sx={{ pt: "2px" }}
              onChange={(e: any) => handleCheckBoxChange(e, i)}
            />
            <Stack sx={{ flex: 1, pr: "4px", textAlign: "left" }}>
              <Typography variant="body2" showBold color={tokens.Text.General.PrimaryHeadline}>
                {i?.name}
              </Typography>
              <Typography variant="label" color={tokens.Text.General.Tertiary} sx={{ mt: "4px" }}>
                {i?.innCode}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Box>
    </>
  ) : (
    <Autocomplete
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          // @ts-ignore
          event.defaultMuiPrevented = true;
          if (searchOnKeyPress) {
            onSearch(event);
          }
        }
      }}
      size={size}
      inputValue={inputValue}
      fullWidth
      freeSolo
      popupIcon=""
      onChange={handleChange}
      onInputChange={(e, value, reason) => handleInputChange(e, value, reason)}
      filterOptions={(x) => x}
      getOptionLabel={(option: any) => option.innCode || ""}
      options={inputValue ? options : []}
      renderOption={(props, option) => (
        <li {...props}>
          <Stack>
            <Typography variant="body2">{option?.innCode}</Typography>
            <Typography variant="label">{option?.name}</Typography>
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={{ backgroundColor: tokens.Background.General.Primary }}
          placeholder={placeholder || "Enter the inncode"}
          onBlur={() => dispatch(setInncodes([]))}
          InputProps={{
            ...params.InputProps,
            startAdornment: startIcon,
            endAdornment: <>{params.InputProps.endAdornment}</>
          }}
        />
      )}
    />
  );
};

export default AutocompleteInncode;
